import React from "react";
import { Autocomplete, TextField } from "@mui/material";
import type { Job } from "../../../../../api/types";
import { getValidationErrorKey } from "../../../../../utilities/jobValidation";
import { columnDisplayNames } from "../../../../admin/ImportSettings";
import { ColumnContext } from "../standard-column-types";
import { DealOption } from "./Deal.column.types";
import { getDealOptionLabel } from "./Deal.column.helpers";
import { useDealColumnViewModel } from "./Deal.column.vm";
import { useValidationErrors } from "../../ValidationErrorsContext";

export interface DealColumnMobileProps {
	/** The job being edited */
	job: Job;
	/** Column context for updates and validation */
	columnContext: ColumnContext;
	/** Whether the field is required */
	required?: boolean;
	/** Whether the field is disabled */
	disabled?: boolean;
	/** Custom label override */
	label?: string;
}

/**
 * Mobile-friendly Deal column component.
 * Allows selection of a deal configuration for the job.
 */
export const DealColumnMobile: React.FC<DealColumnMobileProps> = ({
	job,
	columnContext,
	required = false,
	disabled = false,
	label,
}) => {
	const vm = useDealColumnViewModel(columnContext);
	const validationErrors = useValidationErrors();
	const allDeals = columnContext.props.allDeals || [];

	// Get current deal using viewmodel
	const currentDeal = vm.getCurrentDeal(job);

	const errorKey = getValidationErrorKey("deal", job.id);

	const handleChange = (_event: React.SyntheticEvent, newDeal: DealOption | null) => {
		if (disabled) return;
		// Use viewmodel - handles dealIds update, field application, and validation
		vm.handleDealChange(job.id, newDeal);
	};

	const options: DealOption[] = [...allDeals, { id: "custom" }];

	return (
		<Autocomplete
			size="small"
			fullWidth
			disabled={disabled}
			value={currentDeal}
			options={options}
			isOptionEqualToValue={(option, value) => option.id === value.id}
			getOptionKey={(option) => option.id}
			getOptionLabel={getDealOptionLabel}
			onChange={handleChange}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label || columnDisplayNames.deal}
					required={required}
					error={!!validationErrors?.[errorKey]}
					helperText={validationErrors?.[errorKey]}
				/>
			)}
		/>
	);
};
